import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { ArrowUpFromLine, Send, FileText, Camera } from "lucide-react";
import { API, portalHeaders } from "../helpers.js";
import DropZone from "@/components/shared/DropZone";

const ACCEPTED = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt,.heic";
const MAX_MB = 25;
// Batch #14.244 — libellés adaptés au tactile (pas de glisser-déposer sur téléphone)
const IS_TOUCH = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

export default function SendTab({ sentComms, primaryColor, onReload }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});
  const cameraRef = useRef(null);

  const handleSend = async () => {
    const errs = {};
    if (!title.trim()) errs.title = "Titre requis";
    if (!files.length && !message.trim()) errs.files = "Au moins un fichier ou un message";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSending(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("message", message);
      fd.append("comm_type", "document");
      files.forEach(f => fd.append("files", f));
      await axios.post(`${API}/portal/communications`, fd, {
        headers: { ...portalHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Document envoye");
      setTitle(""); setMessage(""); setFiles([]); setErrors({});
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="tab-envoyer">
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
          <ArrowUpFromLine className="w-4 h-4" /> Envoyer un document au cabinet
        </h3>
        <div className="space-y-3">
          <div>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.title ? "border-red-300 bg-red-50" : ""}`}
              placeholder="Titre du document *" maxLength={200} data-testid="portal-send-title" />
            {errors.title && <p className="text-xs text-red-500 mt-0.5">{errors.title}</p>}
          </div>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3}
            className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
            placeholder="Message (optionnel)" maxLength={2000} data-testid="portal-send-message" />

          <DropZone
            onFilesAdded={(added) => setFiles([...files, ...added])}
            files={files}
            onRemove={(i) => setFiles(files.filter((_, j) => j !== i))}
            accept={ACCEPTED}
            maxSize={MAX_MB * 1024 * 1024}
            compact
            label={IS_TOUCH ? "Touchez pour choisir un fichier" : "Glissez vos fichiers ici"}
            sublabel={IS_TOUCH ? "PDF, photos, Word, Excel…" : "ou cliquez pour selectionner"}
            testIdPrefix="portal-send-drop"
          />
          {errors.files && <p className="text-xs text-red-500 mt-0.5">{errors.files}</p>}

          <div className="flex flex-col sm:flex-row gap-2">
            <button type="button" onClick={() => cameraRef.current?.click()}
              className="sm:hidden flex items-center justify-center gap-2 border rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-50"
              data-testid="portal-send-camera-btn">
              <Camera className="w-4 h-4" /> Prendre une photo du document
            </button>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setFiles([...files, f]); e.target.value = ""; }} />
            <button onClick={handleSend} disabled={sending}
              className="w-full sm:w-auto flex items-center justify-center gap-2 text-white px-4 py-2.5 sm:py-2 rounded-lg text-sm font-medium transition disabled:opacity-50"
              style={{ backgroundColor: primaryColor }} data-testid="portal-send-btn">
              <Send className="w-4 h-4" /> {sending ? "Envoi..." : "Envoyer"}
            </button>
          </div>
        </div>
      </div>

      {sentComms.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Mes envois</h3>
          {sentComms.map(comm => (
            <div key={comm.id} className="bg-white rounded-xl shadow-sm border p-4 mb-2" data-testid={`sent-${comm.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ArrowUpFromLine className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{comm.title}</p>
                  {comm.message && <p className="text-sm text-gray-600 mt-1">{comm.message}</p>}
                  <p className="text-[10px] text-gray-400 mt-1">{new Date(comm.created_at).toLocaleDateString("fr-CA")}</p>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">{comm.status}</span>
              </div>
              {(comm.attachments || []).length > 0 && (
                <div className="ml-11 mt-2 space-y-1.5">
                  {comm.attachments.map(att => (
                    <div key={att.id} className="flex items-center gap-2 text-xs text-gray-500">
                      <FileText className="w-3.5 h-3.5" /> {att.filename}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
